import { Pagination, PaginationResponse, Permission, Role } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit, Group } from '@mui/icons-material';
import { Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminTable, AdminTableColumn, Highlight } from 'src/Common/Components/AdminTable';
import { RowActionsMenu } from 'src/Common/Components/RowActionsMenu';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import useRequest from 'src/Common/Hooks/useRequest';
import Permissions from 'src/Common/Permissions';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { SettingsSection } from 'src/SettingsSection/Components/SettingsSection';
import { RolesAdminDrawer } from '../../RoleManagement/Components/RolesAdminDrawer';
import { useSettingsSection } from '../Providers/SettingsSectionProvider';

export const RolesAdminSection = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { setDrawerOpen, isDrawerOpen } = useSettingsSection();
  const navigate = useNavigate();
  const { refresh: triggerRefresh } = usePagination();

  const [, startSaving] = useRequest<void>();
  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);
  const [, startFetchingPerms] = useRequest<Permission[]>();

  const [selectedRole, setSelectedRole] = useState<Role | undefined>();

  const [hasRolesWrite, hasUsersRead] = useMemo(
    () => [hasPermission(Permissions.ROLES_WRITE), hasPermission(Permissions.USERS_READ)],
    [hasPermission],
  );

  useEffect(() => {
    startFetchingPerms(
      () => api.getPermissions(),
      (result) => setAvailablePermissions(result ?? []),
    );
  }, [api, startFetchingPerms]);

  const fetchItems = useCallback(
    (pagination: Pagination): Promise<PaginationResponse<Role>> => api.getRoles(pagination),
    [api],
  );

  const dismiss = useCallback(() => {
    setDrawerOpen(false);
    setSelectedRole(undefined);
  }, []);

  const openDrawer = useCallback((role?: Role) => {
    setSelectedRole(role);
    setDrawerOpen(true);
  }, []);

  const saveHandler = useCallback(
    (payload: Role | Partial<Role>) => {
      startSaving(
        async () => {
          if (payload?.id === undefined && selectedRole === undefined) {
            await api.addRole(payload as Role);
          } else {
            await api.updateRole(payload as Role);
          }
        },
        () => {
          enqueueSnackbar(selectedRole ? 'Role updated.' : 'Role created.');
          dismiss();
          triggerRefresh();
        },
      );
    },
    [api, selectedRole, startSaving, triggerRefresh],
  );

  const onDeleteConfirm = useCallback(() => {
    if (selectedRole === undefined) {
      return;
    }
    startSaving(
      () => api.deleteRole(selectedRole),
      () => {
        dismiss();
        triggerRefresh();
      },
    );
  }, [api, selectedRole, startSaving, triggerRefresh]);

  const columns = useMemo<AdminTableColumn<Role>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        size: 220,
        renderCell: (row) => (
          <Typography fontWeight={600} noWrap>
            <Highlight text={row.name} field={'name'} />
          </Typography>
        ),
      },
      {
        field: 'coordinate',
        headerName: 'Coordinate',
        size: 300,
        renderCell: (row) => (
          <Typography variant={'body2'} color={'text.secondary'} noWrap>
            <Highlight text={row.coordinate} field={'coordinate'} />
          </Typography>
        ),
      },
      {
        field: 'permissions',
        headerName: 'Permissions',
        size: 320,
        sortable: false,
        filterable: false,
        renderCell: (row) => {
          const perms = row.permissions ?? [];
          if (perms.length === 0) {
            return (
              <Typography variant={'caption'} color={'text.disabled'}>
                none
              </Typography>
            );
          }
          const preview = perms.slice(0, 2);
          const remaining = perms.length - preview.length;
          return (
            <Tooltip
              arrow
              title={
                <Stack spacing={0.25} sx={{ p: 1 }}>
                  {[...perms].sort().map((p) => (
                    <Typography key={p} variant={'caption'} fontFamily={'monospace'}>
                      {p}
                    </Typography>
                  ))}
                </Stack>
              }
            >
              <Stack direction={'row'} spacing={0.5} alignItems={'center'} flexWrap={'wrap'}>
                {preview.map((p) => (
                  <Chip
                    key={p}
                    label={p}
                    size={'small'}
                    variant={'outlined'}
                    sx={{ height: 22, fontFamily: 'monospace', fontSize: 11 }}
                  />
                ))}
                {remaining > 0 && (
                  <Chip
                    label={`+${remaining}`}
                    size={'small'}
                    sx={{ height: 22, fontSize: 11, backgroundColor: 'action.selected', color: 'text.secondary' }}
                  />
                )}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: '__actions',
        headerName: '',
        size: 60,
        sortable: false,
        filterable: false,
        resizable: false,
        hideInColumnsPopover: true,
        align: 'center',
        renderCell: (row) => (
          <RowActionsMenu<Role>
            row={row}
            actions={[
              {
                label: 'Users',
                icon: <Group fontSize="small" />,
                onClick: (r) => navigate('/me/admin/roles/' + r.id + '/users'),
                disabled: () => !hasUsersRead,
              },
              {
                label: 'Edit',
                icon: <Edit fontSize="small" />,
                onClick: openDrawer,
                disabled: () => !hasRolesWrite,
              },
              {
                label: 'Delete',
                icon: <Delete fontSize="small" />,
                onClick: (r) => {
                  dismiss();
                  setSelectedRole(r);
                },
                disabled: () => !hasRolesWrite,
              },
            ]}
          />
        ),
      },
    ],
    [hasUsersRead, hasRolesWrite, navigate],
  );

  return (
    <SettingsSection
      title={'Roles'}
      description={'Define roles and their permission sets.'}
      drawer={
        <RolesAdminDrawer
          role={selectedRole}
          availablePermissions={availablePermissions}
          saveHandler={saveHandler}
          onClose={dismiss}
        />
      }
    >
      <AdminTable<Role>
        fetchItems={fetchItems}
        columns={columns}
        initialSort={{ field: 'name', sort: 'asc' }}
        sortFieldMap={{
          name: 'R.name',
          coordinate: 'Node.materializedPath',
        }}
        primaryAction={
          hasRolesWrite ? (
            <Button variant="contained" startIcon={<Add />} onClick={() => openDrawer(undefined)}>
              Add role
            </Button>
          ) : undefined
        }
      />

      <GenericDialog
        type={'confirmation'}
        maxWidth={'sm'}
        fullWidth
        centered
        open={selectedRole !== undefined && !isDrawerOpen}
        title={'Delete Role'}
        text={selectedRole ? `Delete Role ${selectedRole.name}?` : ''}
        onClose={dismiss}
        onConfirm={onDeleteConfirm}
      />
    </SettingsSection>
  );
};
